<script lang="ts">
  import { onMount } from 'svelte';
  import type { AdminClientOverride } from '$lib/models/alii-features/types';
  import { SlideToggle, Tab, TabGroup } from '@skeletonlabs/skeleton';
  import { X } from 'phosphor-svelte';
  import { featureFlagStore } from '$lib/stores/featureFlagStore';
  import IconButton from '$lib/components/common/iconButton/IconButton.svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import Checkbox from '$lib/components/common/checkbox/Checkbox.svelte';

  export let name: string;
  export let clientOverrides: AdminClientOverride[];
  // Overrides as last persisted. The client search endpoint always hides these, so any removed
  // (but not yet saved) ones are merged back into the search results client-side.
  export let savedClientOverrides: AdminClientOverride[] = [];

  const searchPageSize = 50;
  const searchDebounceMs = 400;

  const pasteLookupConcurrency = 5;

  type ClientOption = { clientId: number; clientName: string };
  type PasteRow = { clientId: number; clientName: string | null; status: 'new' | 'existing' | 'notFound' | 'checking' };
  type AddMode = 'search' | 'paste';

  // Existing overrides: filtering and bulk selection
  let overrideFilter: string = '';
  let selectedOverrideIds: Set<number> = new Set();

  $: filteredOverrides = filterOverrides(clientOverrides, overrideFilter);
  // Drop selections that are hidden by the filter (or removed) so bulk actions only touch visible rows
  $: selectedOverrideIds = pruneSelection(selectedOverrideIds, filteredOverrides);
  $: allFilteredSelected = filteredOverrides.length > 0 && filteredOverrides.every((co) => selectedOverrideIds.has(co.clientId));

  // New clients: multi-select search
  let searchInput: string = '';
  let searchResults: ClientOption[] = [];
  let searching: boolean = false;
  let searchRequestId: number = 0;
  let searchTimeoutId: ReturnType<typeof setTimeout> | undefined;
  let pendingClients: ClientOption[] = [];
  let newClientsEnabled: boolean = false;

  $: pendingIds = new Set(pendingClients.map((c) => c.clientId));
  $: allResultsPending = searchResults.length > 0 && searchResults.every((c) => pendingIds.has(c.clientId));

  // New clients: pasted list of client IDs
  let addMode: AddMode = 'search';
  let pasteInput: string = '';
  let pasteLookupTimeoutId: ReturnType<typeof setTimeout> | undefined;
  // Client ID -> name, or null if no such client exists
  let lookupCache: Map<number, string | null> = new Map();
  let lookupsInFlight: Set<number> = new Set();
  let lookupFailedIds: Set<number> = new Set();

  $: parsedPaste = parsePastedIds(pasteInput);
  $: pasteRows = parsedPaste.ids.map((id) => classifyPastedId(id, clientOverrides, savedClientOverrides, lookupCache));
  $: pasteNewRows = pasteRows.filter((r) => r.status === 'new');
  $: pasteExistingRows = pasteRows.filter((r) => r.status === 'existing');
  $: pasteNotFoundRows = pasteRows.filter((r) => r.status === 'notFound');
  $: pasteChecking = pasteRows.some((r) => r.status === 'checking');
  $: pasteApplyCount = pasteNewRows.length + pasteExistingRows.length;

  onMount(() => {
    searchClients();
  });

  function filterOverrides(overrides: AdminClientOverride[], filter: string): AdminClientOverride[] {
    const term = filter.trim().toLowerCase();
    if (!term) return overrides;
    return overrides.filter((co) => co.clientId.toString().includes(term) || (co.clientName ?? '').toLowerCase().includes(term));
  }

  function pruneSelection(selection: Set<number>, visible: AdminClientOverride[]): Set<number> {
    const visibleIds = new Set(visible.map((co) => co.clientId));
    const pruned = [...selection].filter((id) => visibleIds.has(id));
    return pruned.length === selection.size ? selection : new Set(pruned);
  }

  function toggleOverrideSelection(clientId: number): void {
    const next = new Set(selectedOverrideIds);
    if (next.has(clientId)) next.delete(clientId);
    else next.add(clientId);
    selectedOverrideIds = next;
  }

  function toggleSelectAllOverrides(): void {
    selectedOverrideIds = allFilteredSelected ? new Set() : new Set(filteredOverrides.map((co) => co.clientId));
  }

  function setOverridesActive(clientIds: Set<number>, isActive: boolean): void {
    clientOverrides = clientOverrides.map((co) => (clientIds.has(co.clientId) ? { ...co, isActive } : co));
  }

  function removeOverrides(clientIds: Set<number>): void {
    const removed = clientOverrides.filter((co) => clientIds.has(co.clientId)).map((co) => ({ clientId: co.clientId, clientName: co.clientName ?? '' }));
    clientOverrides = clientOverrides.filter((co) => !clientIds.has(co.clientId));
    // Update the results locally rather than re-searching, so the list responds immediately
    searchResults = mergeIntoResults(searchResults, removed, searchInput);
  }

  function onSearchInput(): void {
    clearTimeout(searchTimeoutId);
    searchTimeoutId = setTimeout(searchClients, searchDebounceMs);
  }

  async function searchClients(): Promise<void> {
    const requestId = ++searchRequestId;
    searching = true;
    try {
      const excludedIds = clientOverrides.map((co) => co.clientId);
      const results = await featureFlagStore.fetchClients(name, searchInput, excludedIds, searchPageSize);
      // Ignore responses from searches that have since been superseded
      if (requestId !== searchRequestId) return;
      // Overrides may have changed while the request was in flight, so exclude against the current list
      const currentIds = new Set(clientOverrides.map((co) => co.clientId));
      const fetched = results.filter((x) => !currentIds.has(x.id)).map((x) => ({ clientId: x.id, clientName: x.name }));
      const removedSaved = savedClientOverrides.filter((co) => !currentIds.has(co.clientId)).map((co) => ({ clientId: co.clientId, clientName: co.clientName ?? '' }));
      searchResults = mergeIntoResults(fetched, removedSaved, searchInput);
    } catch {
    } finally {
      if (requestId === searchRequestId) searching = false;
    }
  }

  // Mirrors the backend search: a numeric term is an exact ID match, otherwise a name "contains" match
  function matchesSearch(client: ClientOption, search: string): boolean {
    const term = search.trim();
    if (!term) return true;
    if (/^-?\d+$/.test(term)) return client.clientId === Number(term);
    return client.clientName.toLowerCase().includes(term.toLowerCase());
  }

  // Adds clients matching the search term to the results (skipping ones already listed), sorted by name
  function mergeIntoResults(results: ClientOption[], clients: ClientOption[], search: string): ClientOption[] {
    const resultIds = new Set(results.map((c) => c.clientId));
    const toAdd = clients.filter((c) => !resultIds.has(c.clientId) && matchesSearch(c, search));
    if (toAdd.length === 0) return results;
    return [...results, ...toAdd].sort((a, b) => a.clientName.localeCompare(b.clientName) || a.clientId - b.clientId);
  }

  function togglePendingClient(client: ClientOption): void {
    pendingClients = pendingIds.has(client.clientId) ? pendingClients.filter((c) => c.clientId !== client.clientId) : [...pendingClients, client];
  }

  function toggleSelectAllResults(): void {
    if (allResultsPending) {
      const resultIds = new Set(searchResults.map((c) => c.clientId));
      pendingClients = pendingClients.filter((c) => !resultIds.has(c.clientId));
    } else {
      pendingClients = [...pendingClients, ...searchResults.filter((c) => !pendingIds.has(c.clientId))];
    }
  }

  function addPendingClients(): void {
    const existingIds = new Set(clientOverrides.map((co) => co.clientId));
    clientOverrides = [
      ...clientOverrides,
      ...pendingClients
        .filter((c) => !existingIds.has(c.clientId))
        .map((c) => ({
          clientId: c.clientId,
          clientName: c.clientName,
          isActive: newClientsEnabled,
        })),
    ];
    const addedIds = new Set(pendingClients.map((c) => c.clientId));
    searchResults = searchResults.filter((c) => !addedIds.has(c.clientId));
    pendingClients = [];
    newClientsEnabled = false;
  }

  function parsePastedIds(input: string): { ids: number[]; invalid: string[] } {
    const tokens = input.split(/[\s,]+/).filter(Boolean);
    const ids = new Set<number>();
    const invalid = new Set<string>();
    for (const token of tokens) {
      if (/^\d+$/.test(token) && Number(token) > 0) ids.add(Number(token));
      else invalid.add(token);
    }
    return { ids: [...ids], invalid: [...invalid] };
  }

  function classifyPastedId(clientId: number, overrides: AdminClientOverride[], saved: AdminClientOverride[], cache: Map<number, string | null>): PasteRow {
    const current = overrides.find((co) => co.clientId === clientId);
    if (current) return { clientId, clientName: current.clientName, status: 'existing' };
    // Saved overrides are hidden by the search endpoint, but we already know they exist
    const savedOverride = saved.find((co) => co.clientId === clientId);
    if (savedOverride) return { clientId, clientName: savedOverride.clientName, status: 'new' };
    if (!cache.has(clientId)) return { clientId, clientName: null, status: 'checking' };
    const clientName = cache.get(clientId) ?? null;
    return { clientId, clientName, status: clientName === null ? 'notFound' : 'new' };
  }

  function onPasteInput(): void {
    if (lookupFailedIds.size > 0) {
      lookupFailedIds.forEach((id) => lookupCache.delete(id));
      lookupFailedIds.clear();
      lookupCache = lookupCache;
    }
    clearTimeout(pasteLookupTimeoutId);
    pasteLookupTimeoutId = setTimeout(lookupPastedIds, searchDebounceMs);
  }

  // Looks up the names of pasted IDs we don't know yet (which also confirms the clients exist)
  async function lookupPastedIds(): Promise<void> {
    const queue = pasteRows.filter((r) => r.status === 'checking' && !lookupsInFlight.has(r.clientId)).map((r) => r.clientId);
    queue.forEach((id) => lookupsInFlight.add(id));

    async function worker(): Promise<void> {
      for (let clientId = queue.shift(); clientId !== undefined; clientId = queue.shift()) {
        try {
          const results = await featureFlagStore.fetchClients(name, clientId.toString(), [], 1);
          lookupCache.set(clientId, results.find((x) => x.id === clientId)?.name ?? null);
        } catch {
          // Show as not found for now, but retry on the next edit
          lookupCache.set(clientId, null);
          lookupFailedIds.add(clientId);
        } finally {
          lookupsInFlight.delete(clientId);
          lookupCache = lookupCache;
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(pasteLookupConcurrency, queue.length) }, worker));
  }

  function applyPastedIds(): void {
    const newIds = new Set(pasteNewRows.map((r) => r.clientId));
    setOverridesActive(new Set(pasteExistingRows.map((r) => r.clientId)), newClientsEnabled);
    clientOverrides = [
      ...clientOverrides,
      ...pasteNewRows.map((r) => ({
        clientId: r.clientId,
        clientName: r.clientName,
        isActive: newClientsEnabled,
      })),
    ];
    searchResults = searchResults.filter((c) => !newIds.has(c.clientId));
    pendingClients = pendingClients.filter((c) => !newIds.has(c.clientId));
    // Leave anything that couldn't be applied in the box so it can be corrected
    pasteInput = [...pasteNotFoundRows.map((r) => r.clientId.toString()), ...parsedPaste.invalid].join('\n');
    newClientsEnabled = false;
  }

  function onAddClick(): void {
    if (addMode === 'search') addPendingClients();
    else applyPastedIds();
  }

  function plural(count: number, noun: string): string {
    return `${count} ${noun}${count === 1 ? '' : 's'}`;
  }

  $: addButtonLabel =
    addMode === 'search'
      ? pendingClients.length > 0
        ? `Add ${plural(pendingClients.length, 'client')}`
        : 'Add'
      : pasteChecking
        ? 'Checking IDs...'
        : pasteApplyCount > 0
          ? `Apply to ${plural(pasteApplyCount, 'client')}`
          : 'Apply';
  $: addButtonDisabled = addMode === 'search' ? pendingClients.length === 0 : pasteChecking || pasteApplyCount === 0;
</script>

<div>
  <div class="grid w-full grid-cols-2 gap-5">
    <div class="rounded-md border p-5">
      <div class="mb-5 text-lg font-bold text-primary-500">
        Client Overrides <span class="font-normal">({clientOverrides.length})</span>
      </div>
      <input class="input mb-3" type="search" placeholder="Filter by name or ID..." bind:value={overrideFilter} autocomplete="off" />
      {#if selectedOverrideIds.size > 0}
        <div class="mb-3 flex flex-wrap items-center gap-2 rounded-md bg-primary-500/10 p-2">
          <span class="flex-grow font-bold text-primary-500">{selectedOverrideIds.size} selected</span>
          <Button label="Enable" colour="success" onClick={() => setOverridesActive(selectedOverrideIds, true)} />
          <Button label="Disable" variant="outlined" onClick={() => setOverridesActive(selectedOverrideIds, false)} />
          <Button label="Remove" colour="error" onClick={() => removeOverrides(selectedOverrideIds)} />
          <Button label="Clear" variant="outlined" onClick={() => (selectedOverrideIds = new Set())} />
        </div>
      {/if}
      <div class="grid grid-cols-12 gap-1">
        <div class="col-span-1 content-center">
          <Checkbox checked={allFilteredSelected} disabled={filteredOverrides.length === 0} onChange={toggleSelectAllOverrides} />
        </div>
        <div class="col-span-2 font-bold text-primary-500">Client ID</div>
        <div class="col-span-6 font-bold text-primary-500">Name</div>
        <div class="col-span-3 font-bold text-primary-500">Enabled</div>
        {#each filteredOverrides as override (override.clientId)}
          <div class="col-span-1 content-center">
            <Checkbox checked={selectedOverrideIds.has(override.clientId)} onChange={() => toggleOverrideSelection(override.clientId)} />
          </div>
          <div class="col-span-2 content-center">
            {override.clientId}
          </div>
          <div class="col-span-6 content-center truncate">
            {override.clientName ?? '—'}
          </div>
          <div class="col-span-3 inline-flex items-center">
            <SlideToggle
              name="enabled"
              size="sm"
              checked={override.isActive}
              on:change={() => setOverridesActive(new Set([override.clientId]), !override.isActive)}
              active="bg-success-500"
              background="bg-secondary-600"
            />
            <IconButton icon="x" onClick={() => removeOverrides(new Set([override.clientId]))} />
          </div>
        {:else}
          <div class="col-span-12 py-2 text-secondary-500">
            {clientOverrides.length === 0 ? 'No client overrides.' : 'No client overrides match the filter.'}
          </div>
        {/each}
      </div>
    </div>
    <div class="rounded-md border p-5">
      <div class="mb-5 text-lg font-bold text-primary-500">Add New Clients</div>
      <div class="flex flex-col gap-5">
        <TabGroup border="border-b">
          <Tab bind:group={addMode} name="addModeSearch" value="search">Search</Tab>
          <Tab bind:group={addMode} name="addModePaste" value="paste">Paste IDs</Tab>
        </TabGroup>
        {#if addMode === 'search'}
          <div>
            <input class="input mb-2" type="search" placeholder="Search by name or ID..." bind:value={searchInput} on:input={onSearchInput} autocomplete="off" />
            <div class="rounded-md border p-2">
              {#if searchResults.length > 0}
                <div class="mb-1 border-b px-1 pb-1">
                  <Checkbox label="Select all results ({searchResults.length})" checked={allResultsPending} onChange={toggleSelectAllResults} />
                </div>
                <div class="max-h-64 overflow-y-auto px-1">
                  {#each searchResults as client (client.clientId)}
                    <div class="py-1">
                      <Checkbox label="{client.clientName} (ID: {client.clientId})" checked={pendingIds.has(client.clientId)} onChange={() => togglePendingClient(client)} />
                    </div>
                  {/each}
                </div>
              {:else}
                <div class="py-1 text-secondary-500">{searching ? 'Searching...' : 'No matching clients without an override.'}</div>
              {/if}
            </div>
          </div>
          <div>
            <div class="mb-2 font-bold text-primary-500">To add ({pendingClients.length})</div>
            {#if pendingClients.length > 0}
              <div class="flex flex-wrap gap-2">
                {#each pendingClients as client (client.clientId)}
                  <span class="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm">
                    {client.clientName} ({client.clientId})
                    <button type="button" class="hover:text-error-500" aria-label="Remove {client.clientName}" on:click={() => togglePendingClient(client)}>
                      <X size="12px" />
                    </button>
                  </span>
                {/each}
              </div>
            {:else}
              <div class="text-secondary-500">Tick clients above to queue them. Selections are kept between searches.</div>
            {/if}
          </div>
        {:else}
          <div>
            <textarea
              class="textarea border-surface-700"
              rows="6"
              placeholder="Paste client IDs, separated by commas, spaces or new lines"
              bind:value={pasteInput}
              on:input={onPasteInput}
            />
            {#if parsedPaste.ids.length > 0 || parsedPaste.invalid.length > 0}
              <div class="mt-2 flex flex-col gap-1 text-sm">
                {#if pasteChecking}
                  <div class="text-secondary-500">Checking {plural(pasteRows.filter((r) => r.status === 'checking').length, 'ID')}...</div>
                {/if}
                {#if pasteNewRows.length > 0}
                  <div class="text-success-600">{plural(pasteNewRows.length, 'new override')} will be added</div>
                {/if}
                {#if pasteExistingRows.length > 0}
                  <div>{plural(pasteExistingRows.length, 'existing override')} will be updated</div>
                {/if}
                {#if pasteNotFoundRows.length > 0}
                  <div class="text-error-500">Not found: {pasteNotFoundRows.map((r) => r.clientId).join(', ')}</div>
                {/if}
                {#if parsedPaste.invalid.length > 0}
                  <div class="text-error-500">Not a valid ID: {parsedPaste.invalid.join(', ')}</div>
                {/if}
              </div>
            {/if}
            {#if pasteNewRows.length + pasteExistingRows.length > 0}
              <div class="mt-2 max-h-48 overflow-y-auto rounded-md border p-2 text-sm">
                {#each [...pasteNewRows, ...pasteExistingRows] as row (row.clientId)}
                  <div class="flex justify-between gap-2 py-0.5">
                    <span class="truncate">{row.clientName ?? '—'} ({row.clientId})</span>
                    <span class="shrink-0 {row.status === 'new' ? 'text-success-600' : 'text-secondary-500'}">{row.status === 'new' ? 'New' : 'Update'}</span>
                  </div>
                {/each}
              </div>
            {/if}
          </div>
        {/if}
        <div class="inline-flex items-center gap-5">
          Enabled
          <div class="align-middle">
            <SlideToggle name="enabled" size="sm" bind:checked={newClientsEnabled} active="bg-success-500" background="bg-secondary-600" />
          </div>
        </div>
        <div>
          <Button colour="success" label={addButtonLabel} size="large" onClick={onAddClick} disabled={addButtonDisabled} />
        </div>
      </div>
    </div>
  </div>
</div>
